import {
  createRouter,
  createWebHistory,
  NavigationGuardNext,
  RouteLocationNormalized,
} from 'vue-router';
import store from './store';
import DefaultLayout from './layouts/Default.vue';
import { User } from './models/User';
import { hasAnyRole, hasAllRoles } from './utils/AuthGuard';

// Lazy-load views that aren't on the home route — they only need to
// be fetched when the user navigates to them.
const Login = () => import('./views/Login.vue');
const AuthHandler = () => import('./views/AuthHandler.vue');
const ResetPassword = () => import('./views/ResetPassword.vue');
const UserManagementView = () => import('./views/UserManagementView.vue');
const MetricsDashboardView = () => import('./views/MetricsDashboardView.vue');
const ModerationView = () => import('./views/ModerationView.vue');
const GroupAssignmentView = () => import('./views/GroupAssignmentView.vue');
const StatusView = () => import('./views/StatusView.vue');
const SupportLibraryView = () => import('./views/SupportLibraryView.vue');
const Maintenance = () => import('./views/Maintenance.vue');

const isMaintenanceMode = process.env.VUE_APP_MAINTENANCE_MODE === 'true';

/**
 * Reusable per-route navigation guard.
 *
 * Handles three concerns in order:
 *  1. Auth0 callback redirect (hash contains tokens)
 *  2. Session hydration from localStorage (deserialise + token refresh)
 *  3. Role-based access control via `to.meta.allowedAnyRoles` / `to.meta.allowedAllRoles`
 *
 * `allowedAnyRoles` – user needs at least ONE of the listed roles (OR).
 * `allowedAllRoles` – user needs ALL of the listed roles (AND).
 * Both can be combined on the same route.
 * If neither is specified the user just needs to be authenticated.
 */
async function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  // 1. OAuth callback — forward the hash to AuthHandler
  if (to.hash) {
    next({ name: 'authenticated', hash: to.hash, query: to.query });
    return;
  }

  // 2. Must be logged in
  if (!store.state.user || !('token' in store.state.user)) {
    next({ name: 'loginRedirect', params: { redirect: to.name as string } });
    return;
  }

  // Rehydrate User class from plain localStorage object
  if (!(store.state.user instanceof User)) {
    const u = store.state.user as any;
    const user = new User(
      u.id,
      u.name,
      u.roles ?? u.role,
      u.token,
      u.tokenExpiry
    );
    if (!(await user.refreshToken())) {
      next({ name: 'loginRedirect', params: { redirect: to.name as string } });
      return;
    }
  } else if (!store.state.user.isAuthenticated()) {
    await store.dispatch('login', null);
    next({ name: 'loginRedirect', params: { redirect: to.name as string } });
    return;
  }

  if (!store.state.passwordStatusLoaded) {
    try {
      await store.dispatch('refreshPasswordStatus');
    } catch {
      next({ name: 'changePasswordRequired' });
      return;
    }
  }

  continueNavigation();

  function continueNavigation() {
    if (
      store.state.mustChangePassword &&
      to.name !== 'changePasswordRequired'
    ) {
      next({ name: 'changePasswordRequired' });
      return;
    }
    if (
      !store.state.mustChangePassword &&
      to.name === 'changePasswordRequired'
    ) {
      next({ name: 'home' });
      return;
    }

    // 3. Role check
    // allowedAllRoles: user must hold ALL listed roles (AND)
    const allowedAllRoles = to.meta.allowedAllRoles as string[] | undefined;
    if (allowedAllRoles && allowedAllRoles.length > 0) {
      if (!hasAllRoles(store.state.user as any, ...allowedAllRoles)) {
        next({ name: 'home' });
        return;
      }
    }

    // allowedAnyRoles: user must hold at least ONE listed role (OR)
    const allowedAnyRoles = to.meta.allowedAnyRoles as string[] | undefined;
    if (allowedAnyRoles && allowedAnyRoles.length > 0) {
      if (!hasAnyRole(store.state.user as any, ...allowedAnyRoles)) {
        next({ name: 'home' });
        return;
      }
    }

    next();
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '',
      component: DefaultLayout,
      name: 'home',
      beforeEnter: authGuard,
    },
    {
      path: '/authenticated',
      name: 'authenticated',
      component: AuthHandler,
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
    },
    {
      path: '/reset',
      name: 'reset',
      component: ResetPassword,
    },
    {
      path: '/change-password',
      name: 'changePasswordRequired',
      component: () => import('./views/MandatoryPasswordChange.vue'),
      beforeEnter: authGuard,
    },
    {
      path: '/login/:redirect',
      name: 'loginRedirect',
      component: Login,
    },
    {
      path: '/users',
      name: 'userManagement',
      component: UserManagementView,
      meta: { allowedAnyRoles: ['admin', 'superadmin'] },
      beforeEnter: authGuard,
    },
    {
      path: '/status',
      name: 'serviceStatus',
      component: StatusView,
      beforeEnter: authGuard,
    },
    {
      path: '/support',
      name: 'supportLibrary',
      component: SupportLibraryView,
      beforeEnter: authGuard,
    },
    {
      path: '/admin/group-assignment',
      name: 'groupAssignment',
      component: GroupAssignmentView,
      meta: { allowedAnyRoles: ['superadmin'] },
      beforeEnter: authGuard,
    },
    {
      path: '/admin/metrics',
      name: 'metricsAdmin',
      component: MetricsDashboardView,
      meta: {
        allowedAllRoles: ['metrics'],
        allowedAnyRoles: ['admin', 'superadmin'],
      },
      beforeEnter: authGuard,
    },
    {
      path: '/moderation',
      name: 'moderation',
      component: ModerationView,
      meta: { allowedAnyRoles: ['moderator'] },
      beforeEnter: authGuard,
    },
    {
      path: '/maintenance',
      name: 'maintenance',
      component: Maintenance,
    },
  ],
});

router.beforeEach((to, from, next) => {
  if (isMaintenanceMode && to.name !== 'maintenance') {
    next({ name: 'maintenance' });
  } else if (!isMaintenanceMode && to.name === 'maintenance') {
    next({ name: 'home' });
  } else {
    next();
  }
});

export default router;
